import { useState } from 'react';
import { Truck, Scale } from '../../lib/icons';
import { toast } from 'sonner';
import { cloturerTare, decompte, nomTiers } from '../../lib/dataProvider';
import { fmt, ParcoursBadge } from '../shared/badges';

const heure = (iso) => (iso ? new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—');

// File d'attente pont-bascule : camions pesés en brut, tare saisie à la sortie.
export const CamionsSurSite = ({ camions, onCloture }) => {
  const [tares, setTares] = useState({});
  if (!camions.length) return null;

  const cloturer = async (r) => {
    const { error, row } = await cloturerTare(r.id, tares[r.id]);
    if (error) return toast.error(error);
    toast.success(`Ticket ${r.id} clôturé — poids net ${fmt(decompte(row).poidsNet)} kg`);
    setTares((p) => ({ ...p, [r.id]: '' }));
    onCloture(row);
  };

  return (
    <section data-testid="camions-sur-site" className="border border-violet-200 border-l-4 border-l-violet-600 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-violet-100 px-4 py-3">
        <h2 className="flex items-center gap-2 font-heading text-base font-bold tracking-tight text-slate-900">
          <Truck size={16} className="text-violet-700" /> Camions sur site — en attente de tare
          <span data-testid="camions-sur-site-count" className="border border-violet-300 bg-violet-50 px-1.5 font-data text-xs text-violet-700">{camions.length}</span>
        </h2>
        <p className="text-xs text-slate-500">Saisissez la tare au retour du camion vide : le net est calculé et le bon est clôturé.</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              {['Ticket / immatriculation', 'Entrée', 'Parcours', 'Tiers', 'Brut (Kg)', 'Tare (Kg)', 'Net estimé', ''].map((c, i) => (
                <th key={c || i} className="px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {camions.map((r) => {
              const t = Number(tares[r.id]);
              const net = tares[r.id] !== undefined && tares[r.id] !== '' ? Math.max(0, Number(r.poidsBrut) - t) : null;
              return (
                <tr key={r.id} data-testid={`camion-${r.id}`} className="hover:bg-violet-50/40">
                  <td className="px-3 py-2 font-data text-sm font-semibold text-slate-900">{r.id}{r.immatriculation && <p className="text-xs text-violet-700">{r.immatriculation}</p>}</td>
                  <td className="px-3 py-2 font-data text-sm text-slate-700">{r.date} {heure(r.heureEntree)}</td>
                  <td className="px-3 py-2"><ParcoursBadge parcours={r.parcours} /></td>
                  <td className="px-3 py-2 text-sm text-slate-800">{nomTiers(r.tiersId)}</td>
                  <td className="tabular px-3 py-2 font-data text-sm text-slate-900">{fmt(r.poidsBrut)}</td>
                  <td className="px-3 py-2">
                    <input
                      data-testid={`tare-input-${r.id}`}
                      type="number"
                      min="0"
                      placeholder="Poids à vide"
                      value={tares[r.id] ?? ''}
                      onChange={(e) => setTares((p) => ({ ...p, [r.id]: e.target.value }))}
                      onKeyDown={(e) => e.key === 'Enter' && cloturer(r)}
                      className="h-9 w-32 border border-slate-300 bg-slate-50 px-2 font-data text-sm outline-none focus:border-violet-600 focus:bg-white"
                    />
                  </td>
                  <td data-testid={`net-estime-${r.id}`} className="tabular px-3 py-2 font-data text-sm font-semibold text-slate-900">
                    {net === null ? '—' : fmt(net)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      data-testid={`cloturer-tare-${r.id}`}
                      onClick={() => cloturer(r)}
                      className="inline-flex h-9 items-center gap-1.5 bg-violet-700 px-3 text-xs font-bold text-white transition-colors duration-150 hover:bg-violet-800"
                    >
                      <Scale size={13} /> Tare (Sortie) & clôturer
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};
